import { createContext } from 'react';

import type { Integration } from '~/interfaces/integration';
import type { ModalType } from '~/interfaces/modal_type';
import type { PendingGroupSubscriptionInvite } from '~/services/api/subscription/listPendingGroupSubscriptionInvites.server';

/**
 * Logic layer for the account **profile / password** surface on the General tab — the profile
 * card + dropdown, and the edit-profile and set/reset-password modals. Self-calls the
 * reset-password + resend-change-email hooks and reads `integrations` from `IdentitiesProvider`
 * for the card's social-login links, then exposes the resulting data + openers via context.
 * Holds no UI; the colocated `AccountProfileSection` and `AccountProfileModals` children render
 * from it. Connected-accounts/identity fetching, subscription lifecycle, and billing are
 * separate domains and stay out.
 */
export type AccountProfileContextType = {
  // --- Profile display (AccountProfileSection) ---
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string;
  hasEmailChangePending: boolean;
  createdAt: string;
  premiumSince?: string;
  showProfileActions: boolean;
  canResetPassword: boolean;
  socialLoginProviders: Integration[];
  isProfileLoading: boolean;
  pendingGroupInvites: PendingGroupSubscriptionInvite[];

  // --- Openers ---
  onClickEditProfile: () => void;
  onClickConnections: () => void;
  onClickResetPassword: () => void;
  onClickLogout: () => void;

  // --- Edit-profile / reset-password modals (AccountProfileModals) ---
  isLoggedIn: boolean;
  subscriberId: string;
  currentModal: ModalType | null;
  closeAccountModal: () => void;
  hasSetPassword: boolean;
  onResend: () => void;
  isResendingConfirmationEmail: boolean;
};

export const AccountProfileContext = createContext<AccountProfileContextType | undefined>(undefined);
AccountProfileContext.displayName = 'AccountProfileContext';
